// ui/pages/StorageDebugPage.tsx
// ✅ 覆蓋全文
// 修正重點：
// 1) 不再使用 FileUploadKit（因為你 repo 的 FileUploadKitProps 沒有 onFiles，造成 ts2322）
//    → 改成原生 <input type="file">，保證過編譯
// 2) 補齊缺少元件：MailQueueMonitor / SealLocationCheck / AiSmokeTest / EmailDebugSection
// 3) AiAnalyzeResult 型別補齊 extractedFields，避免 TS 報錯

import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { isSuperUser } from "../config/roles";
import { functions, db } from "../../firebaseClient";

import { DashboardTodosLiveDebugTab } from "../components/debug/DashboardTodosLiveDebugTab";
import { DashboardTodosDebugTab } from "../components/debug/DashboardTodosDebugTab";

// Casting to any avoids TS prop-mismatch build breaks when the signature differs across versions.
import type { NavKey } from "../Sidebar";

import { extractPlainTextFromFile, ExtractedResult } from "../utils/fileTextExtractor";

// ✅ repo 內未必有 AiAnalyzeResult export，這裡用最小相容型別
type AiAnalyzeResult = {
  summary?: string;
  tags?: string[];
  extractedFields?: Record<string, any>;
  rawText?: string;
  model?: string;
  usage?: any;
  [k: string]: any;
};

const TabButton = ({ label, active, onClick }: any) => (
  <button
    onClick={onClick}
    style={{
      padding: "10px 12px",
      borderRadius: 10,
      border: "1px solid rgba(255,255,255,0.12)",
      background: active ? "rgba(255,255,255,0.10)" : "transparent",
      color: "rgba(255,255,255,0.92)",
      cursor: "pointer",
      fontSize: 13,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </button>
);

export default function StorageDebugPage(props: { setNavKey?: (k: NavKey) => void }) {
  const { user } = useAuth() as any;
  const toast = useToast() as any;

  const isAdmin = useMemo(() => isSuperUser?.(user?.email ?? ""), [user?.email]);

  const [tab, setTab] = useState<"live" | "data" | "queue" | "seal" | "ai" | "email">("data");

  useEffect(() => {
    props?.setNavKey?.(("STORAGE_DEBUG" as unknown) as NavKey);
  }, [props]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 18, color: "rgba(255,255,255,0.92)" }}>
        <h2 style={{ margin: "6px 0 10px" }}>系統檢測與工具（Debug）</h2>
        <div style={{ opacity: 0.85, lineHeight: 1.6 }}>
          這一頁僅限超級帳號使用。你現在登入的帳號沒有權限。
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 18, color: "rgba(255,255,255,0.92)" }}>
      <h2 style={{ margin: "6px 0 14px" }}>系統檢測與工具（Debug）</h2>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <TabButton label="今日待辦摘要 Live 連線偵錯" active={tab === "live"} onClick={() => setTab("live")} />
        <TabButton label="待辦數據一致性 (Data)" active={tab === "data"} onClick={() => setTab("data")} />
        <TabButton label="事件佇列 (Queue)" active={tab === "queue"} onClick={() => setTab("queue")} />
        <TabButton label="SEAL 落點檢測" active={tab === "seal"} onClick={() => setTab("seal")} />
        <TabButton label="AI 分析測試" active={tab === "ai"} onClick={() => setTab("ai")} />
        <TabButton label="Email 發送測試" active={tab === "email"} onClick={() => setTab("email")} />
      </div>

      <div
        style={{
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 14,
          padding: 14,
          background: "rgba(0,0,0,0.20)",
        }}
      >
        {tab === "live" && <DashboardTodosLiveDebugTab />}
        {tab === "data" && <DashboardTodosDebugTab />}

        {tab === "queue" && <MailQueueMonitor toast={toast} />}
        {tab === "seal" && <SealLocationCheck toast={toast} />}
        {tab === "ai" && <AiSmokeTest toast={toast} />}
        {tab === "email" && <EmailDebugSection toast={toast} user={user} />}
      </div>
    </div>
  );
}

// -------------------- 補齊缺少元件（保底，不擋 build） --------------------

function MailQueueMonitor({ toast }: any) {
  const [queueName, setQueueName] = useState("lexi-line-events");
  const [result, setResult] = useState<any>(null);

  const peek = async () => {
    try {
      setResult(null);

      const fn = (functions as any)?.httpsCallable ? (functions as any).httpsCallable("debugPeekQueue") : null;
      if (!fn) {
        setResult({ ok: false, note: "未配置 debugPeekQueue callable（保底提示）" });
        toast?.push?.("Queue 檢測：未配置 callable（保底提示）");
        return;
      }

      const res = await fn({ queue: queueName });
      setResult(res?.data ?? res);
      toast?.push?.("Queue 檢測完成");
    } catch (e: any) {
      setResult({ ok: false, error: String(e?.message ?? e) });
      toast?.push?.("Queue 檢測失敗");
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>事件佇列（Queue）</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input
          value={queueName}
          onChange={(e) => setQueueName(e.target.value)}
          placeholder="Queue name"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
            minWidth: 240,
          }}
        />
        <button
          onClick={peek}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
          }}
        >
          立即檢測
        </button>
      </div>

      <pre style={{ margin: 0, opacity: 0.9, whiteSpace: "pre-wrap" }}>{JSON.stringify(result, null, 2)}</pre>
    </div>
  );
}

function SealLocationCheck({ toast }: any) {
  const [docId, setDocId] = useState("");
  const [result, setResult] = useState<any>(null);

  const run = async () => {
    try {
      setResult(null);

      if (!docId.trim()) {
        toast?.push?.("請先輸入 docId");
        return;
      }

      const col = (db as any)?.collection?.("sealRequests");
      if (!col) {
        setResult({ ok: false, note: "db.collection 不可用（可能是 modular SDK）；此區塊保底不擋 build" });
        toast?.push?.("SEAL 檢測：db.collection 不可用（保底提示）");
        return;
      }

      const snap = await col.doc(docId.trim()).get();
      setResult({ ok: true, exists: snap?.exists, data: snap?.data?.() ?? null });
      toast?.push?.("SEAL 落點檢測完成");
    } catch (e: any) {
      setResult({ ok: false, error: String(e?.message ?? e) });
      toast?.push?.("SEAL 落點檢測失敗");
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>SEAL 落點檢測</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input
          value={docId}
          onChange={(e) => setDocId(e.target.value)}
          placeholder="輸入 sealRequests docId"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
            minWidth: 260,
          }}
        />
        <button
          onClick={run}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
          }}
        >
          檢測
        </button>
      </div>

      <pre style={{ margin: 0, opacity: 0.9, whiteSpace: "pre-wrap" }}>{JSON.stringify(result, null, 2)}</pre>
    </div>
  );
}

function AiSmokeTest({ toast }: any) {
  const [file, setFile] = useState<File | null>(null);
  const [extract, setExtract] = useState<ExtractedResult | null>(null);
  const [result, setResult] = useState<AiAnalyzeResult | null>(null);

  const doExtract = async () => {
    try {
      if (!file) {
        toast?.push?.("請先選擇檔案");
        return;
      }
      const ex = await extractPlainTextFromFile(file);
      setExtract(ex);
      toast?.push?.("已擷取文字，可進一步做 AI 分析");
    } catch (e: any) {
      toast?.push?.("擷取文字失敗");
      setExtract({ text: "", meta: { error: String(e?.message ?? e) } } as any);
    }
  };

  const doAnalyze = async () => {
    try {
      setResult(null);
      if (!extract?.text?.trim()) {
        toast?.push?.("請先擷取文字");
        return;
      }

      const fn = (functions as any)?.httpsCallable ? (functions as any).httpsCallable("aiAnalyzeText") : null;
      if (!fn) {
        setResult({
          summary: "（保底）未配置 aiAnalyzeText callable；擷取文字成功。",
          extractedFields: {},
        });
        toast?.push?.("AI 分析：未配置 callable（保底提示）");
        return;
      }

      const res = await fn({ text: extract.text.slice(0, 20000) });
      setResult((res?.data ?? res) as any);
      toast?.push?.("AI 分析完成");
    } catch (e: any) {
      setResult({ summary: "", extractedFields: { error: String(e?.message ?? e) } });
      toast?.push?.("AI 分析失敗");
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>AI 分析測試</div>

      <div style={{ marginBottom: 10 }}>
        {/* ✅ 原生 input：不依賴 FileUploadKitProps（避免 onFiles prop mismatch） */}
        <input
          type="file"
          accept="*/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
            width: "100%",
            maxWidth: 640,
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <button
          onClick={doExtract}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
          }}
        >
          擷取文字
        </button>
        <button
          onClick={doAnalyze}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
          }}
        >
          送 AI 分析
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 600, opacity: 0.9, marginBottom: 6 }}>Extract</div>
          <pre style={{ margin: 0, opacity: 0.9, whiteSpace: "pre-wrap" }}>{JSON.stringify(extract, null, 2)}</pre>
        </div>
        <div>
          <div style={{ fontWeight: 600, opacity: 0.9, marginBottom: 6 }}>AI Result</div>
          <pre style={{ margin: 0, opacity: 0.9, whiteSpace: "pre-wrap" }}>{JSON.stringify(result, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}

function EmailDebugSection({ toast, user }: any) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("LegalOps 測試信");
  const [body, setBody] = useState("這是一封測試信（Debug頁發送）。");
  const [result, setResult] = useState<any>(null);

  const send = async () => {
    try {
      setResult(null);

      const fn = (functions as any)?.httpsCallable ? (functions as any).httpsCallable("debugSendEmail") : null;
      if (!fn) {
        setResult({ ok: false, note: "未配置 debugSendEmail callable（保底提示）" });
        toast?.push?.("Email：未配置 callable（保底提示）");
        return;
      }

      const res = await fn({
        to: to.trim(),
        subject,
        body,
        fromUser: user?.email ?? null,
      });

      setResult(res?.data ?? res);
      toast?.push?.("Email 發送完成");
    } catch (e: any) {
      setResult({ ok: false, error: String(e?.message ?? e) });
      toast?.push?.("Email 發送失敗");
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>Email 發送測試</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, maxWidth: 640 }}>
        <input
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="收件人 email"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
          }}
        />
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="主旨"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
          }}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="內文"
          rows={5}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.25)",
            color: "rgba(255,255,255,0.92)",
            resize: "vertical",
          }}
        />

        <button
          onClick={send}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.08)",
            color: "rgba(255,255,255,0.92)",
            cursor: "pointer",
            width: 140,
          }}
        >
          發送
        </button>
      </div>

      <pre style={{ marginTop: 12, marginBottom: 0, opacity: 0.9, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}
